import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import SiteWalkModal from "../components/SiteWalkModal";
import {
  Sparkles, Droplets, Building2, ShieldCheck, Wind, SprayCan,
  Snowflake, CloudLightning, Newspaper, Camera, Lock, DollarSign,
  Star, ArrowUpRight, Phone, MapPin, Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Founding-Partner benefits (14 total — mirrors the printed flyer)
const BENEFITS = [
  { icon: Sparkles,       label: "Window Cleaning",           sub: "Every 2 weeks" },
  { icon: Droplets,       label: "Pressure Washing",          sub: "Quarterly" },
  { icon: Building2,      label: "Exterior Facade & Awning",  sub: "Monthly" },
  { icon: SprayCan,       label: "Entryway Sanitizing",       sub: "Every visit" },
  { icon: Wind,           label: "Sidewalk Sweep & Clean",    sub: "Biweekly" },
  { icon: ShieldCheck,    label: "Graffiti Removal",          sub: "Within 24 hours" },
  { icon: Snowflake,      label: "Snow & Salt Service",       sub: "Winter months" },
  { icon: CloudLightning, label: "Inclement Weather Alerts",  sub: "Automatic" },
  { icon: Newspaper,      label: "Monthly Newsletter",        sub: "Portfolio insights" },
  { icon: Camera,         label: "Monthly Exterior Report",   sub: "With photos" },
  { icon: Lock,           label: "Price Locked",              sub: "For life" },
  { icon: DollarSign,     label: "$300 Cash Referral",        sub: "Per annual client" },
];

// Trust ribbon items along the very bottom
const RIBBON = [
  "Veteran Owned",
  "Reliable & On Time",
  "Premium Service",
  "Detailed Reports",
  "Price Locked for Life",
  "Your Partner in Success",
];

export default function FoundingPartner() {
  const [count, setCount] = useState(87);      // spots remaining (server-hydrated)
  const [claimed, setClaimed] = useState(13);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    property_address: "", property_city: "", property_state: "NY", property_zip: "",
    property_size: "medium", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [walkOpen, setWalkOpen] = useState(false);

  // Fetch live counter from backend (real scarcity — updates as founding leads roll in)
  useEffect(() => {
    axios.get(`${API}/founding-partner/stats`).then((r) => {
      setCount(r.data.remaining);
      setClaimed(r.data.claimed);
    }).catch(() => {});
  }, [sent]);

  function change(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        services: ["Founding Partner Program"],
        property_type: "Commercial",
        property_size: form.property_size,
        property_address: form.property_address,
        property_city: form.property_city,
        property_state: form.property_state,
        property_zip: form.property_zip,
        message: `FOUNDING PARTNER INQUIRY — ${form.message || "Ready to lock in $297–$497/mo price for life."}`,
        source: "founding-partner-landing",
      });
      setSent(true);
      toast.success("Your spot is being held. A founder will call within 1 business hour.");
    } catch {
      toast.error("Transmission failed — please call (833) 8BESPOKE.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ background: "#0a0a0c" }}>
      <SiteWalkModal open={walkOpen} onClose={() => setWalkOpen(false)} />
      {/* ---------------------- HERO ---------------------- */}
      <section className="relative overflow-hidden" style={{ minHeight: "92vh" }}>
        {/* Hero image — positioned to feature the handshake at top-right */}
        <div
          className="absolute inset-0 hidden md:block"
          style={{
            backgroundImage: "url(/brand/founding-partner-hero.png)",
            backgroundSize: "auto 130%",
            backgroundPosition: "right -20%",
            backgroundRepeat: "no-repeat",
            filter: "brightness(0.9)",
          }}
          data-testid="founding-hero-image"
        />
        {/* Mobile fallback — subtle background */}
        <div
          className="absolute inset-0 md:hidden"
          style={{
            background:
              "radial-gradient(ellipse at top right, rgba(197,160,89,0.15) 0%, rgba(6,6,8,1) 60%)",
          }}
        />
        {/* Layered gradients — heavy on the left for legibility, subtle vignette on the right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(6,6,8,1) 0%, rgba(6,6,8,0.98) 32%, rgba(6,6,8,0.92) 46%, rgba(6,6,8,0.62) 58%, rgba(6,6,8,0.15) 74%, rgba(6,6,8,0.0) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,6,8,0.35) 0%, rgba(6,6,8,0) 25%, rgba(6,6,8,0) 75%, rgba(6,6,8,0.75) 100%)",
          }}
        />

        <div className="relative max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14 py-20 md:py-28 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
            className="lg:col-span-7"
          >
            {/* Trust chips */}
            <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="founding-trust-chips">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em]"
                style={{
                  border: "1px solid rgba(197,160,89,0.55)",
                  color: "var(--gold)",
                  borderRadius: 999,
                  background: "rgba(197,160,89,0.08)",
                }}
              >
                <Star size={11} strokeWidth={2} /> Disabled Veteran Owned
              </span>
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em]"
                style={{
                  border: "1px solid rgba(255,255,255,0.14)",
                  color: "rgba(255,255,255,0.85)",
                  borderRadius: 999,
                }}
              >
                Only 100 Spots · <span style={{ color: "var(--gold)" }}>{count} left</span> · <span style={{ color: "var(--text-3)" }}>{claimed} claimed</span>
              </span>
            </div>

            {/* Kicker */}
            <div className="font-mono text-[11px] uppercase tracking-[0.5em] mb-6" style={{ color: "var(--gold)" }}>
              Bespoke Exterior Solutions · Founding 100
            </div>

            {/* H1 */}
            <h1
              className="font-display text-white"
              style={{
                fontSize: "clamp(2.75rem, 6.2vw, 5.5rem)",
                lineHeight: 0.98,
                letterSpacing: "-0.025em",
              }}
              data-testid="founding-headline"
            >
              Founding
              <br />
              <span
                style={{
                  background: "linear-gradient(180deg, #EFC97B 0%, #C5A059 55%, #8A6E37 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Partner Program.
              </span>
            </h1>

            {/* Subhead */}
            <p
              className="mt-7 font-body max-w-2xl"
              style={{ fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)", lineHeight: 1.55, color: "rgba(255,255,255,0.85)" }}
              data-testid="founding-subhead"
            >
              A full-service exterior maintenance retainer — window cleaning every two weeks, pressure washing, façade care, sidewalk & entry sanitizing, 24-hour graffiti response, monthly photo reports.
              <br className="hidden md:block" />
              <span style={{ color: "var(--gold-2)" }}>Locked at Founding pricing for life.</span>
            </p>

            {/* Price block */}
            <div
              className="mt-10 inline-flex items-baseline gap-4"
              style={{
                border: "1px solid rgba(197,160,89,0.35)",
                background: "rgba(197,160,89,0.06)",
                padding: "22px 28px",
                borderRadius: 20,
              }}
              data-testid="founding-price-block"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--gold)" }}>
                From
              </div>
              <div
                className="font-display text-white"
                style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", letterSpacing: "-0.03em", lineHeight: 1 }}
              >
                $297
                <span style={{ fontSize: "0.55em", color: "var(--gold-2)" }}> – $497</span>
                <span style={{ fontSize: "0.35em", color: "rgba(255,255,255,0.65)" }}> /mo</span>
              </div>
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "var(--text-3)" }}>
              Priced by property size · After the 100th partner, rates return to $597 – $797/mo
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#claim"
                className="inline-flex items-center gap-3 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] transition-transform duration-300 hover:scale-[1.03]"
                style={{
                  background: "linear-gradient(180deg, #EFC97B 0%, #C5A059 100%)",
                  color: "#0b0b0d",
                  borderRadius: 999,
                  fontWeight: 600,
                }}
                data-testid="founding-cta-claim"
                data-cursor="hover"
              >
                Claim My Founding Spot <ArrowUpRight size={14} />
              </a>
              <a
                href="tel:+18338237765"
                className="inline-flex items-center gap-3 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] transition-colors duration-300"
                style={{
                  border: "1px solid rgba(255,255,255,0.28)",
                  color: "rgba(255,255,255,0.95)",
                  borderRadius: 999,
                }}
                data-testid="founding-cta-call"
                data-cursor="hover"
              >
                <Phone size={12} /> (833) 8BESPOKE
              </a>
              <button
                type="button"
                onClick={() => setWalkOpen(true)}
                className="inline-flex items-center gap-3 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] transition-colors duration-300"
                style={{
                  border: "1px solid rgba(255,255,255,0.14)",
                  color: "rgba(255,255,255,0.75)",
                  borderRadius: 999,
                }}
                data-testid="founding-cta-site-walk"
                data-cursor="hover"
              >
                Book a Site Walk First
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------------------- WHAT'S INCLUDED ---------------------- */}
      <section className="relative py-24 md:py-32" style={{ background: "#0b0b0d" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="max-w-3xl mb-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.5em] mb-5" style={{ color: "var(--gold)" }}>
              What's Included
            </div>
            <h2 className="font-display text-white" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1.02, letterSpacing: "-0.02em" }}>
              Twelve premium services.
              <br />
              <span style={{ color: "var(--gold-2)" }} className="font-display-italic">One monthly retainer.</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="founding-benefits-grid">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                  className="p-6 md:p-7 transition-colors duration-300"
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(197,160,89,0.14)",
                    borderRadius: 18,
                  }}
                >
                  <div
                    className="w-11 h-11 flex items-center justify-center mb-5"
                    style={{
                      background: "rgba(197,160,89,0.10)",
                      border: "1px solid rgba(197,160,89,0.28)",
                      borderRadius: 12,
                    }}
                  >
                    <Icon size={18} style={{ color: "var(--gold)" }} strokeWidth={1.5} />
                  </div>
                  <div className="font-display text-white text-lg leading-tight" style={{ letterSpacing: "-0.01em" }}>
                    {b.label}
                  </div>
                  <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--gold-2)" }}>
                    {b.sub}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------------- GUARANTEE ---------------------- */}
      <section className="py-16 md:py-20" style={{ background: "#0a0a0c" }}>
        <div className="max-w-[1100px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="p-8 md:p-14 text-center"
            style={{
              border: "1px solid rgba(197,160,89,0.32)",
              background: "linear-gradient(135deg, rgba(197,160,89,0.08) 0%, rgba(197,160,89,0.02) 100%)",
              borderRadius: 28,
            }}
            data-testid="founding-guarantee"
          >
            <ShieldCheck size={40} style={{ color: "var(--gold)" }} strokeWidth={1.4} className="mx-auto mb-6" />
            <div className="font-mono text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: "var(--gold)" }}>
              100% Satisfaction Guaranteed
            </div>
            <h3 className="font-display text-white" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", letterSpacing: "-0.02em" }}>
              If you're not satisfied, we make it right.
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}> Period.</span>
            </h3>
            <p className="mt-5 font-body max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
              You approve every visit through a photo-documented report. If a job doesn't meet your standard, we return the same week — at no additional cost.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ---------------------- CLAIM FORM ---------------------- */}
      <section id="claim" className="py-24 md:py-32" style={{ background: "#0b0b0d" }}>
        <div className="max-w-[1300px] mx-auto px-6 sm:px-10 lg:px-14 grid lg:grid-cols-12 gap-14">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.5em] mb-5" style={{ color: "var(--gold)" }}>
              Reserve Your Spot
            </div>
            <h2 className="font-display text-white" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.04, letterSpacing: "-0.02em" }}>
              Ask about availability
              <br />
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>for your block.</span>
            </h2>
            <p className="mt-6 font-body" style={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.65, fontSize: 16 }}>
              After the 100th partner, monthly pricing returns to <strong style={{ color: "white" }}>$597–$797</strong>.
              <br /><br />
              Founding partners keep their rate <strong style={{ color: "var(--gold-2)" }}>for the life of the contract</strong> — through every renewal, every service expansion, every year.
            </p>

            <div className="mt-10 space-y-4 font-mono text-[11px]" style={{ color: "rgba(255,255,255,0.75)" }}>
              <div className="flex items-center gap-3">
                <Phone size={13} style={{ color: "var(--gold)" }} />
                <a href="tel:+18338237765" className="hover:text-white">(833) 8BESPOKE · Toll-free</a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={13} style={{ color: "var(--gold)" }} />
                <a href="tel:+15182472467" className="hover:text-white">(518) 247-2467 · Local</a>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={13} style={{ color: "var(--gold)" }} />
                <a
                  href="https://maps.google.com/?q=185+Great+Neck+Rd+4th+Floor+Great+Neck+NY+11021"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                  data-testid="founding-address"
                >
                  185 Great Neck Rd, 4th Fl · Great Neck, NY 11021
                </a>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="p-10 md:p-14 text-center"
                style={{
                  border: "1px solid rgba(197,160,89,0.42)",
                  background: "linear-gradient(135deg, rgba(197,160,89,0.10) 0%, rgba(197,160,89,0.02) 100%)",
                  borderRadius: 26,
                }}
                data-testid="founding-form-success"
              >
                <Check size={44} style={{ color: "var(--gold)" }} strokeWidth={1.4} className="mx-auto mb-6" />
                <h3 className="font-display text-white text-3xl md:text-4xl" style={{ letterSpacing: "-0.02em" }}>
                  Your spot is being held.
                </h3>
                <p className="mt-5 font-body max-w-lg mx-auto" style={{ color: "rgba(255,255,255,0.8)" }}>
                  A founder will call within one business hour to confirm your Founding Partner rate and schedule your first site walk.
                </p>
                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--gold-2)" }}>
                  Welcome to the Founding 100.
                </p>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                className="p-8 md:p-10 space-y-5"
                style={{
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.02)",
                  borderRadius: 24,
                }}
                data-testid="founding-form"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  <FormField label="Full Name" name="name" value={form.name} onChange={change} required />
                  <FormField label="Company" name="company" value={form.company} onChange={change} />
                  <FormField label="Email" type="email" name="email" value={form.email} onChange={change} required />
                  <FormField label="Phone" name="phone" value={form.phone} onChange={change} required />
                </div>
                <FormField label="Property Street Address" name="property_address" value={form.property_address} onChange={change} placeholder="e.g. 145 Broadway" />
                <div className="grid md:grid-cols-3 gap-5">
                  <FormField label="City" name="property_city" value={form.property_city} onChange={change} placeholder="New York" />
                  <FormField label="State" name="property_state" value={form.property_state} onChange={change} />
                  <FormField label="Zip" name="property_zip" value={form.property_zip} onChange={change} placeholder="10001" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>
                    Property Size (rough guess is fine)
                  </label>
                  <select
                    value={form.property_size}
                    onChange={(e) => change("property_size", e.target.value)}
                    className="w-full bg-transparent px-5 py-4 font-body focus:outline-none"
                    style={{ borderRadius: 14, border: "1px solid rgba(255,255,255,0.14)", color: "white" }}
                    data-testid="founding-form-size"
                  >
                    <option value="small"  style={{ background: "#0b0b0d" }}>Small · under 1,500 sq ft · $297/mo</option>
                    <option value="medium" style={{ background: "#0b0b0d" }}>Medium · 1,500–4,000 sq ft · $397/mo</option>
                    <option value="large"  style={{ background: "#0b0b0d" }}>Large · 4,000+ sq ft · $497/mo</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>
                    Anything we should know? (optional)
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => change("message", e.target.value)}
                    rows={3}
                    placeholder="Timeline · access constraints · specific concerns"
                    className="w-full bg-transparent px-5 py-4 font-body focus:outline-none"
                    style={{ borderRadius: 14, border: "1px solid rgba(255,255,255,0.14)", color: "white" }}
                    data-testid="founding-form-message"
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.32em] max-w-sm" style={{ color: "rgba(255,255,255,0.5)" }}>
                    No credit card required. A founder will call within one business hour.
                  </p>
                  <button
                    type="submit"
                    disabled={sending}
                    className="inline-flex items-center gap-3 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
                    style={{
                      background: "linear-gradient(180deg, #EFC97B 0%, #C5A059 100%)",
                      color: "#0b0b0d",
                      borderRadius: 999,
                      fontWeight: 600,
                    }}
                    data-testid="founding-form-submit"
                    data-cursor="hover"
                  >
                    {sending ? "Reserving…" : "Claim My Founding Spot"} <ArrowUpRight size={14} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ---------------------- TRUST RIBBON ---------------------- */}
      <section className="py-10 border-t" style={{ background: "#0a0a0c", borderColor: "rgba(197,160,89,0.14)" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.4em]" data-testid="founding-ribbon">
            {RIBBON.map((r) => (
              <span key={r} className="inline-flex items-center gap-2" style={{ color: "rgba(255,255,255,0.55)" }}>
                <span style={{ color: "var(--gold)" }}>◆</span> {r}
              </span>
            ))}
          </div>
          <p className="mt-8 text-center font-body text-xs" style={{ color: "rgba(255,255,255,0.32)" }}>
            Prices subject to change. Services and pricing are subject to modification without prior notice. Bespoke Exterior Solutions is a Disabled Veteran Owned Business.
          </p>
        </div>
      </section>
    </div>
  );
}

/** Small internal input helper — DRY the ~7 identical fields */
function FormField({ label, name, value, onChange, type = "text", required = false, placeholder = "" }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.4em] block mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>
        {label} {required && <span style={{ color: "var(--gold)" }}>*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`founding-form-${name}`}
        className="w-full bg-transparent px-5 py-4 font-body focus:outline-none"
        style={{ borderRadius: 14, border: "1px solid rgba(255,255,255,0.14)", color: "white" }}
      />
    </div>
  );
}
